import { useWindowSize } from "hooks/useWindowSize";
import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import { CheckCircleFilled } from "components/icons/CheckCircleFilled";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { ReconcileResult } from "store/apis/reconciliation";
import { colors } from "constants/colors";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Divider } from "components/design/Divider";
import BankWarningCircle from "static/images/BankWarningCircle.svg";
import { NotEqual } from "components/icons/NotEqual";
import { Preview } from "components/PreviewModal";
import { useModal } from "hooks/useModal";
import { useCurrentGroup } from "hooks/useCurrentGroup";

export const ReconciliationConsole = ({
  reconcilTxn,
  difference,
  inkleBooksBalance,
  adjustedMoneyIn,
  adjustedMoneyOut,
}: {
  reconcilTxn: ReconcileResult;
  difference: number;
  inkleBooksBalance: number;
  adjustedMoneyIn: number;
  adjustedMoneyOut: number;
}) => {
  const { uuid: groupId } = useCurrentGroup();
  const { isMobile } = useWindowSize();
  const previewModal = useModal();
  const {
    statement_ending_balance = 0,
    as_of_start_date_balance = 0,
    reconciliation_start_date,
    reconciliation_end_date,
    bank_csv_id,
  } = reconcilTxn || {};

  const differenceZero = Boolean(difference === 0);

  const onPreview = () => {
    previewModal.open();
  };

  return (
    <>
      <div
        className={classNames(
          "t-flex t-flex-col t-gap-5 t-bg-surface t-rounded-lg t-border-solid  t-border t-p-5 sm:t-px-3",
          {
            "t-border-neutral-0": differenceZero,
            "t-border-red-10": !differenceZero,
          }
        )}
      >
        <div className="t-flex t-items-center t-gap-2 sm:t-grid sm:t-grid-cols-[auto_1fr] sm:t-items-start sm:t-gap-y-2">
          {differenceZero ? (
            <CheckCircleFilled className="t-size-[18px] t-text-green-80" />
          ) : (
            <img
              src={BankWarningCircle}
              alt="BankWarningCircle"
              className="t-size-4"
            />
          )}
          {differenceZero ? (
            <div className="t-text-body-sm t-flex t-gap-1 sm:t-min-w-0 sm:t-flex-1 sm:t-flex-col sm:t-gap-0.5 sm:t-text-caption">
              <span className="t-text-green-80 sm:t-whitespace-nowrap">
                Account balances matching:
              </span>
              <span className="t-text-text-30">
                Statement Balance = Inkle Balance
              </span>
            </div>
          ) : (
            <div className="t-text-body-sm t-flex t-gap-1 sm:t-min-w-0 sm:t-flex-1 sm:t-flex-col sm:t-gap-0.5 sm:t-text-caption">
              <span className="t-text-red-70 sm:t-whitespace-nowrap">
                Account balances not matching:
              </span>
              <div>
                Include transactions worth{" "}
                <span className="t-text-subtext sm:t-text-subtext-sm">
                  <AmountSuperScript amount={difference} />
                </span>{" "}
                to make the balances match.
              </div>
            </div>
          )}
          <div className="sm:t-col-start-2 sm:t-justify-self-start">
            <Tag
              tagType="gray"
              icon={false}
              rounded
              size={isMobile ? "small" : "medium"}
            >
              as of {dayjs(reconciliation_end_date).format(DISPLAY_DATE)}
            </Tag>
          </div>
        </div>

        <div className="t-flex t-items-stretch t-gap-8 t-ml-6 sm:t-ml-0 sm:t-flex-col sm:t-gap-3">
          <div className="t-flex t-flex-col t-gap-1 t-max-w-[200px] t-flex-shrink-0 sm:t-max-w-full">
            <div className="t-flex t-items-center t-gap-1">
              <span className="t-text-body-sm-lite t-text-text-30">
                Balance according to Statement
              </span>
            </div>
            <div className="t-text-subtext">
              <AmountSuperScript amount={statement_ending_balance || 0} />
            </div>
            {bank_csv_id && (
              <Button customType="link" size="small" onClick={onPreview}>
                View Statement
              </Button>
            )}
          </div>

          <div className="t-flex t-items-center t-justify-center sm:t-justify-start">
            {differenceZero ? "=" : <NotEqual />}
          </div>

          <div className="t-flex t-flex-col t-gap-1">
            <div className="t-text-body-sm-lite t-text-text-30">
              Balance in Inkle Books
            </div>
            <div className="t-flex t-items-baseline t-gap-2 sm:t-flex-wrap">
              <div className="t-text-subtext t-flex-shrink-0">
                <AmountSuperScript amount={inkleBooksBalance} />
              </div>
              <div className="t-flex t-items-center t-gap-1 t-text-body-sm-italic t-text-text-60 t-flex-shrink-0 sm:t-block sm:t-flex-shrink sm:t-text-caption">
                <span>[</span>
                <div className="t-flex t-items-center t-gap-1 t-text-body-sm-italic t-italic t-text-text-60 t-flex-shrink-0 sm:t-contents">
                  <AmountSuperScript amount={as_of_start_date_balance} />
                  <span className="t-text-text-30 t-whitespace-nowrap">
                    (Inkle Balance as of{" "}
                    {dayjs(reconciliation_start_date).format(DISPLAY_DATE)})
                  </span>
                  <span>+</span>
                  <br className="t-hidden sm:t-block" />
                  <AmountSuperScript amount={adjustedMoneyIn} />
                  <span className="t-text-text-30 t-whitespace-nowrap">
                    (Money In)
                  </span>
                  <span>-</span>
                  <AmountSuperScript amount={Math.abs(adjustedMoneyOut)} />
                  <span className="t-text-text-30 t-whitespace-nowrap">
                    (Money Out)
                  </span>
                </div>

                <span>]</span>
              </div>
            </div>
          </div>

          <div className="t-self-stretch sm:t-hidden">
            <Divider orientation="vertical" color="dark-grey" fullHeight />
          </div>

          <div className="t-flex t-flex-col t-gap-1 ">
            <div className="t-text-body-sm-lite t-text-text-30">Difference</div>
            <div
              className={classNames("t-text-subtitle", {
                "t-text-dark_green": difference === 0,
                "t-text-red": difference !== 0,
              })}
            >
              <AmountSuperScript amount={difference || 0} />
            </div>
          </div>
        </div>
      </div>
      {previewModal.isOpen && (
        <Preview
          showModal={previewModal.isOpen}
          closeModal={previewModal.close}
          groupId={groupId}
          previewId={bank_csv_id}
        />
      )}
    </>
  );
};
